import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Microscope, Wind, Droplets, FlaskConical, ClipboardCheck, ThermometerSun, Building2, Fan } from "lucide-react";
import { SERVICES } from "@/data/services";
import { ARTICLES } from "@/data/articles";
import { INDUSTRIES } from "@/data/industries";
import { SectionHeader, Reveal } from "@/components/SectionHeader";
import SEO from "@/components/SEO";

const HERO_MOTION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const HERO_IMG = "https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGNvbW1lcmNpYWwlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODI3NTAzMzl8MA&ixlib=rb-4.1.0&q=85";
const LUXURY_HOME = "https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const INSPECTOR = "https://images.unsplash.com/photo-1709803857195-dbd9eb6dc4bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxlbmdpbmVlciUyMGluc3BlY3RpbmclMjBidWlsZGluZyUyMHdpdGglMjB0YWJsZXR8ZW58MHx8fHwxNzgyNzUwMzM5fDA&ixlib=rb-4.1.0&q=85";
const LAB = "https://images.unsplash.com/photo-1518152006812-edab29b069ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxzY2llbnRpZmljJTIwbGFiJTIwZXF1aXBtZW50JTIwbWljcm9zY29wZXxlbnwwfHx8fDE3ODI3NTAzMzl8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Independent Environmental Assessments in Texas"
        description="Hurtler Environmental performs independent mold, indoor air quality, moisture, and water-damage assessments across Texas. Headquartered in Edinburg, TX. Schedule today."
        path="/"
      />
      {/* Hero */}
      <section className="relative bg-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/70 via-[#0F172A]/85 to-[#0F172A]" />
        </div>
        <div className="container-x relative pt-28 pb-32 md:pt-40 md:pb-44">
          <motion.div
            {...HERO_MOTION}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-[0.22em] text-[#F59E0B] font-semibold mb-8">
              <ShieldCheck className="w-3.5 h-3.5" />
              Independent • Scientific • Texas
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
              Independent Environmental
              <br />
              Assessments <span className="text-[#F59E0B]">built on science.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              Hurtler Environmental performs unbiased mold, indoor air quality, moisture,
              and water-damage investigations across Texas. We do not remediate — and that
              is the entire point.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/schedule" data-testid="hero-schedule-button" className="btn-primary">
                Schedule Assessment <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/services" data-testid="hero-services-button" className="btn-outline-dark">
                Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-white border-b border-slate-100">
        <div className="container-x py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 text-center">
            {[
              { k: "AIHA-LAP", v: "Accredited Laboratories" },
              { k: "IICRC S520", v: "Mold Remediation Standard" },
              { k: "ASHRAE 62.1", v: "Ventilation Compliance" },
              { k: "S500 / S540", v: "Water & Trauma" },
            ].map((t) => (
              <div key={t.k}>
                <div className="font-display font-extrabold text-xl text-[#0F172A]">{t.k}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">{t.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="section-pad bg-[#F8FAFC]">
        <div className="container-x">
          <SectionHeader
            overline="What we do"
            title="A complete environmental consulting practice."
            subtitle="Field diagnostics, laboratory interpretation, written protocols, and independent verification — all under one roof."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`home-service-card-${s.slug}`}
                    className="card-soft block p-8 h-full"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/10 grid place-items-center mb-6">
                      <Icon className="w-6 h-6 text-[#7C3AED]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-[#0F172A] mb-3">{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{s.summary}</p>
                    <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#7C3AED]">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries served (alternating) */}
      <section className="section-pad bg-white">
        <div className="container-x">
          <SectionHeader
            overline="Industries served"
            title="Trusted across every sector that depends on clean air and dry buildings."
          />
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <img src={LUXURY_HOME} alt="Luxury home" className="w-full h-[420px] object-cover rounded-2xl" />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {INDUSTRIES.slice(0, 8).map((ind, i) => {
                  const Icon = ind.icon;
                  return (
                    <div key={ind.name} data-testid={`industry-card-${i}`} className="card-soft p-5">
                      <Icon className="w-5 h-5 text-[#7C3AED] mb-3" />
                      <div className="font-display font-bold text-[#0F172A] text-sm">{ind.name}</div>
                      <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{ind.blurb}</div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Independence section */}
      <section className="section-pad bg-[#F8FAFC]">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionHeader
              overline="Why independent matters"
              title={<>The data we deliver is the <span className="yellow-underline">only</span> product we sell.</>}
              subtitle="We never remediate. We never accept commissions. The structural separation between investigator and contractor is what makes our findings defensible."
            />
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { k: "0", v: "Remediation services sold" },
                { k: "100%", v: "Independent reporting" },
                { k: "AIHA", v: "Lab partners only" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display font-extrabold text-3xl text-[#0F172A]">
                    <span className="yellow-underline">{s.k}</span>
                  </div>
                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500 mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={INSPECTOR} alt="Inspector with tablet" className="w-full h-[480px] object-cover rounded-2xl" />
          </Reveal>
        </div>
      </section>

      {/* Learning preview */}
      <section className="section-pad bg-white">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeader
              overline="Learning center"
              title="Education first. Sales second."
              subtitle="The largest growing environmental learning library in Texas."
            />
            <Link to="/learning" data-testid="home-view-all-articles" className="btn-secondary text-sm">
              View all articles <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.05}>
                <Link to={`/learning/${a.slug}`} data-testid={`home-article-${a.slug}`} className="card-soft block p-7 h-full">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C3AED]">{a.category}</div>
                  <h3 className="font-display font-bold text-lg text-[#0F172A] mt-3 leading-snug">{a.title}</h3>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{a.excerpt}</p>
                  <div className="mt-6 text-xs text-slate-500">{a.readMin} min read</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0F172A] text-white py-24 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#7C3AED]/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#F59E0B]/10 blur-3xl" />
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-[#F59E0B] mb-5">Ready when you are</div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight">
              Schedule an independent environmental assessment.
            </h2>
            <p className="text-white/70 mt-6 max-w-xl leading-relaxed">
              Most assessments are scheduled within 48–72 hours. Reports typically delivered in 5–7 business days.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Link to="/schedule" data-testid="cta-schedule" className="btn-primary">
              Schedule Assessment <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/contact" data-testid="cta-contact" className="btn-outline-dark">
              Talk to a Consultant
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
